<template>
  <q-page class="admin-courses-page">
    <div class="admin-courses-container">
      <!-- Header -->
      <div class="ac-header">
        <router-link to="/admin" class="ac-back-btn">
          <q-icon name="arrow_back" size="20px" />
        </router-link>
        <div>
          <h1 class="ac-title">Gestion de Cursos</h1>
          <p class="ac-subtitle">{{ courses.length }} cursos en el sistema</p>
        </div>
      </div>

      <!-- Loading -->
      <template v-if="loading">
        <q-skeleton v-for="n in 4" :key="n" type="rect" height="80px" dark style="border-radius: 16px; margin-bottom: 12px;" />
      </template>

      <!-- Error -->
      <div v-else-if="error" class="ac-center-msg">
        <p style="color: var(--oa-danger); margin-bottom: 8px;">{{ error }}</p>
        <q-btn flat no-caps label="Reintentar" style="color: var(--oa-gold-full);" @click="fetchCourses" />
      </div>

      <!-- Empty -->
      <div v-else-if="courses.length === 0" class="ac-center-msg">
        No hay cursos
      </div>

      <!-- Courses list -->
      <div v-else class="ac-list">
        <div v-for="course in courses" :key="course.id" class="ac-course-card">
          <div class="ac-course-left">
            <div
              class="ac-course-icon"
              :style="{
                background: course.status === 'published'
                  ? 'color-mix(in srgb, var(--oa-tier-cc) 12%, transparent)'
                  : 'var(--oa-bg-3)',
                color: course.status === 'published' ? 'var(--oa-tier-cc)' : 'var(--oa-text-muted)',
              }"
            >
              <q-icon name="school" size="20px" />
            </div>
            <div class="ac-course-info">
              <p class="ac-course-title">{{ course.title }}</p>
              <div class="ac-course-meta">
                <q-badge
                  :label="statusLabel(course.status)"
                  :style="{
                    background: statusBg(course.status),
                    color: statusColor(course.status),
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }"
                />
                <span v-if="course.certLevel" class="ac-cert-level">
                  <q-icon name="emoji_events" size="12px" />
                  {{ certLabels[course.certLevel] || course.certLevel }}
                </span>
                <span v-if="course.isFree" class="ac-free-tag">Gratis</span>
                <span v-if="course.priceArs" class="ac-price">${{ course.priceArs }} ARS</span>
              </div>
            </div>
          </div>

          <div class="ac-course-right">
            <router-link
              :to="`/courses/${course.slug}`"
              class="ac-view-btn"
              title="Ver curso"
            >
              <q-icon name="visibility" size="16px" />
            </router-link>
            <q-btn
              no-caps
              unelevated
              dense
              :loading="togglingId === course.id"
              :style="{
                background: course.status === 'published'
                  ? 'color-mix(in srgb, var(--oa-gold-full) 12%, transparent)'
                  : 'color-mix(in srgb, var(--oa-success) 12%, transparent)',
                color: course.status === 'published' ? 'var(--oa-gold-full)' : 'var(--oa-success)',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 500,
                padding: '4px 12px',
              }"
              @click="toggleStatus(course.id)"
            >
              <q-icon
                :name="course.status === 'published' ? 'visibility_off' : 'visibility'"
                size="14px"
                style="margin-right: 4px;"
              />
              {{ course.status === 'published' ? 'Despublicar' : 'Publicar' }}
            </q-btn>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from 'boot/axios';
import { useQuasar } from 'quasar';

const $q = useQuasar();

// ---------- Types ----------
interface Course {
  id: string;
  title: string;
  slug: string;
  status: string;
  certLevel: string | null;
  isFree: boolean;
  priceArs: number | null;
}

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const courses = ref<Course[]>([]);
const togglingId = ref<string | null>(null);

// ---------- Constants ----------
const certLabels: Record<string, string> = {
  cc: 'Coach Certified', sc: 'Senior Coach', c1: 'Coach Instructor',
  c2: 'Lead Trainer', owner: 'Owner', oa: 'Olympic Auditor',
  gj: 'Games Judge', kairos: 'Kairos',
};

// ---------- Helpers ----------
function statusLabel(status: string): string {
  switch (status) {
    case 'published': return 'Publicado';
    case 'draft': return 'Borrador';
    default: return 'Archivado';
  }
}

function statusBg(status: string): string {
  switch (status) {
    case 'published': return 'color-mix(in srgb, var(--oa-success) 12%, transparent)';
    case 'draft': return 'color-mix(in srgb, var(--oa-gold-full) 12%, transparent)';
    default: return 'var(--oa-bg-3)';
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'published': return 'var(--oa-success)';
    case 'draft': return 'var(--oa-gold-full)';
    default: return 'var(--oa-text-muted)';
  }
}

// ---------- Data Fetching ----------
async function fetchCourses() {
  loading.value = true;
  error.value = null;
  try {
    const { data: res } = await api.get('/admin/courses');
    courses.value = res;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar cursos';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

async function toggleStatus(courseId: string) {
  togglingId.value = courseId;
  try {
    const { data: res } = await api.patch(`/admin/courses/${courseId}/status`);
    // Update in place
    const course = courses.value.find((c) => c.id === courseId);
    if (course) course.status = res.status;
    $q.notify({
      type: 'positive',
      message: res.status === 'published' ? 'Curso publicado' : 'Curso despublicado',
    });
  } catch {
    $q.notify({ type: 'negative', message: 'Error al cambiar estado del curso' });
  } finally {
    togglingId.value = null;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchCourses();
});
</script>

<style scoped>
.admin-courses-page {
  padding: 24px;
}

.admin-courses-container {
  max-width: 900px;
  margin: 0 auto;
}

/* Header */
.ac-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.ac-back-btn {
  padding: 8px;
  border-radius: 12px;
  color: var(--oa-text-muted);
  transition: background 0.15s ease;
  text-decoration: none;
}

.ac-back-btn:hover {
  background: var(--oa-bg-3);
}

.ac-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.ac-subtitle {
  font-size: 0.875rem;
  color: var(--oa-text-muted);
  margin: 2px 0 0;
}

.ac-center-msg {
  text-align: center;
  padding: 48px 0;
  color: var(--oa-text-muted);
  font-size: 0.875rem;
}

/* Course list */
.ac-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ac-course-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  transition: box-shadow 0.15s ease;
}

.ac-course-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.ac-course-left {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.ac-course-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ac-course-info {
  min-width: 0;
}

.ac-course-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ac-course-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.ac-cert-level {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.6875rem;
  color: var(--oa-gold-full);
  font-weight: 500;
}

.ac-free-tag {
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--oa-success);
}

.ac-price {
  font-size: 0.6875rem;
  color: var(--oa-text-muted);
}

.ac-course-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.ac-view-btn {
  padding: 8px;
  border-radius: 8px;
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
}

.ac-view-btn:hover {
  background: var(--oa-bg-3);
  color: var(--oa-text-primary);
}
</style>
